import { AuthContext, FireactContext, SetPageTitle } from "../lib/core";
import { Alert, Box, Button, Container, Grid, Paper, Typography } from "@mui/material";
import TextField from '@mui/material/TextField';
import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { Stack } from "@mui/system";
import LoadingButton from '@mui/lab/LoadingButton';
import SaveIcon from '@mui/icons-material/Save';
import { collection, query, where, getDocs, getDoc, doc, setDoc, addDoc, deleteDoc, GeoPoint, Timestamp, orderBy } from 'firebase/firestore';
import Footer from "../template/Footer";
import {PageSection} from "../template/TemplateElements";
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const Payment = ({loader}) => {
  const [orderIds, setOrderIds] = useState([]);

    const { authInstance, firestoreInstance, functionsInstance } = useContext(AuthContext);
    const { config } = useContext(FireactContext);
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const { authUser } = useContext(AuthContext);
  const [piechartData, setPiechartData] = useState([]);
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [totalOrderAmount, setTotalOrderAmount] = useState(0);

  // Stripe connection and balance state
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [hasCheckedConnectionStatus, setHasCheckedConnectionStatus] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [transactionsError, setTransactionsError] = useState(null);
  // Earnings state
  const [earnings, setEarnings] = useState(null);
  const [isLoadingEarnings, setIsLoadingEarnings] = useState(false);
  const [earningsError, setEarningsError] = useState(null);
  // Pending payout state
  const [hasPendingPayout, setHasPendingPayout] = useState(false);
  const [isCheckingPendingPayout, setIsCheckingPendingPayout] = useState(false);

    const sizing = {
        /*legend: { hidden: true },*/
        height: 400,
    };

  useEffect( () => {
        setLoaded(false);
        setError(null);
        // get default permission level name
        let defaultPermission = '';
        for(var permission in config.saas.permissions){
            const value = config.saas.permissions[permission];
            if(value.default){
                defaultPermission = permission;
            }
        }
        
    const fetchData = async () => {
      if (authUser.user) {
        // read from firestore DB
        const schedulesRef = collection(firestoreInstance, '/schedules');
        const scheduleQuery = query(schedulesRef, 
          where('treatment.provider.user_id', '==', authUser.user.uid),
          orderBy("scheduleOrderDate", "desc"),
          orderBy("scheduleOrderTime", "asc"));

        const schedules = [];
        await Promise.all([getDocs(scheduleQuery)]).then(([scheduleSnapshot]) => {
          scheduleSnapshot.forEach(record => {
            const schedule = record.data();
            schedule.id = record.id;
            schedules.push(schedule);
          });
          setLoaded(true);
        }).catch(error => {
          setLoaded(true);
          console.log('error', error);
        });

        if (schedules.length > 0) {
          const orderedProducts = Object.groupBy(schedules, schedule => schedule.treatment.product_id);
          if (orderedProducts) {
            setOrderedProducts(orderedProducts);
          }

          const orderedProductsAmount = Object.keys(orderedProducts).map(key => {
            const orders = orderedProducts[key];
            let amount=0;
            let productName = '';
            orders.map((item, index) => {
              amount += item.totalPrice;
              productName = item.treatment.name;
            });
            return {label:productName, value: amount};
          });
          setPiechartData(orderedProductsAmount);
          // Calculate total order amount
          const total = orderedProductsAmount.reduce((sum, item) => sum + (item.value || 0), 0);
          setTotalOrderAmount(total);
        }
      }
    }

    // Fetch earnings from 'orders' collection
    const fetchEarnings = async () => {
      if (!authUser.user) return;
      setIsLoadingEarnings(true);
      setEarningsError(null);
      try {
        const ordersRef = collection(firestoreInstance, '/orders');
        const ordersQuery = query(
          ordersRef,
          where('metadata.providerId', '==', authUser.user.uid),
          where('status', '==', 'unpaid')
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        let totalEarnings = 0;
        let unpaidOrderIds = [];
        ordersSnapshot.forEach(doc => {
          const data = doc.data();
          if (typeof data.amountReceived === 'number') {
            totalEarnings += data.amountReceived;
          }
          unpaidOrderIds.push(doc.id);
        });
        setEarnings(totalEarnings);
        setOrderIds(unpaidOrderIds);
      } catch (err) {
        setEarningsError(err.message || 'Failed to fetch earnings');
      } finally {
        setIsLoadingEarnings(false);
      }
    };

    fetchData();
    fetchEarnings();
    // Check for pending payout request
    const checkPendingPayout = async () => {
      if (!authUser.user) return;
      setIsCheckingPendingPayout(true);
      try {
        const checkPendingPayoutRequest = httpsCallable(functionsInstance, "checkPendingPayoutRequest");
        const result = await checkPendingPayoutRequest({ userId: authUser.user.uid });
        setHasPendingPayout(result?.data?.pending === true);
      } catch (err) {
        setHasPendingPayout(false); // fallback: allow payout if error
      } finally {
        setIsCheckingPendingPayout(false);
      }
    };
    checkPendingPayout();
    // Check Stripe connection status on mount
    const checkConnectionStatus = async () => {
      if (!authUser.user) {
        setConnectionError("User not authenticated.");
        setHasCheckedConnectionStatus(true);
        return;
      }
      setIsCheckingConnection(true);
      setConnectionError(null);
      try {
        const verifyStripeAccountStatus = httpsCallable(functionsInstance, "verifyStripeAccountStatus");
        const result = await verifyStripeAccountStatus({ userId: authUser.user.uid });
        setHasCheckedConnectionStatus(true);
        setIsCheckingConnection(false);
        if (result && result.data) {
          const { charges_enabled, payouts_enabled } = result.data;
          if (charges_enabled && payouts_enabled) {
            setIsConnected(true);
            // Fetch balance if connected
            setIsLoadingBalance(true);
            setIsLoadingTransactions(true);
            setTransactionsError(null);
            try {
              const getStripeAccountBalance = httpsCallable(functionsInstance, "getStripeAccountBalance");
              const balanceResult = await getStripeAccountBalance({ userId: authUser.user.uid });
              setStripeBalance(balanceResult.data);
              // Fetch transactions
              const listStripeAccountTransactions = httpsCallable(functionsInstance, "listStripeAccountTransactions");
              const txResult = await listStripeAccountTransactions({ userId: authUser.user.uid, limit: 20 });
              setTransactions(txResult.data && txResult.data.transactions ? txResult.data.transactions : []);
            } catch (err) {
              setConnectionError("Failed to fetch Stripe balance or transactions: " + (err.message || "Unknown error"));
              setTransactionsError(err.message || "Failed to fetch transactions");
            } finally {
              setIsLoadingBalance(false);
              setIsLoadingTransactions(false);
            }
          } else {
            setIsConnected(false);
          }
        } else {
          setIsConnected(false);
          setConnectionError("Invalid response from server.");
        }
      } catch (err) {
        setIsCheckingConnection(false);
        setHasCheckedConnectionStatus(true);
        setIsConnected(false);
        setConnectionError("Connection check failed: " + (err.message || "Unknown error"));
      }
    };
    checkConnectionStatus();
        
    },[authInstance, config.saas.permissions, firestoreInstance]);

  return (
    <>
      {loaded ? (
        <Container maxWidth={false} disableGutters>
          <SetPageTitle title="Payment" />
          <Box sx={{ flexGrow: 1, p: 2, width: '100%', minHeight: '100vh'}} >
            <Grid container spacing={0}>
              <Grid xs={12} md={12}>
                <PageSection>
                  <Grid container spacing={0}>
                    <Grid xs={12} md={12} sx={{px: {xs:0, md:2}}}>
                      <Typography component="div" variant="h5" align="center" sx={{pb:1}}>
                        Your Orders{typeof totalOrderAmount === 'number' && !isNaN(totalOrderAmount) ? `: $${totalOrderAmount.toFixed(2)}` : ''}
                      </Typography>
                      <Grid container spacing={0}>
                        <Grid xs={12} md={4}>
                          <Typography component="div" variant="body1" align="center">
                            {piechartData && (
                              <PieChart
                                series={[
                                  {
                                    data: piechartData,
                                    arcLabel: (item) => `$${item.value}`,
                                  },
                                ]}
                                margin={{ top: 10, bottom: 10, left: 70, right:70 }}
                                slotProps={{
                                  legend: {
                                    direction: 'row',
                                    position: { vertical: 'top', horizontal: 'middle' },
                                    padding: 0,
                                  },
                                }}
                                sx={{
                                  [`& .${pieArcLabelClasses.root}`]: {
                                    fill: 'white',
                                    fontSize: 12,
                                  },
                                }}
                                {...sizing}
                              />
                            )}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md={4}>
                          <CollapsibleTable orderedProducts={orderedProducts}/>
                        </Grid>
                        <Grid xs={12} md={4}>
                          <Typography component="div" variant="body1" align="center">
                            <b>Earnings</b>
                            <br/>
                            {isLoadingEarnings ? (
                              <span>Loading earnings...</span>
                            ) : earningsError ? (
                              <Alert severity="error">{earningsError}</Alert>
                            ) : (
                              <span style={{fontSize: '1.5em'}}>
                                {typeof earnings === 'number' ? `$${(earnings/100).toFixed(2)}` : '--'}
                              </span>
                            )}
                            {isConnected && (
                              <>
                                <Box sx={{ mt: 2 }}>
                                  <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={processing || isCheckingPendingPayout}
                                    sx={{ fontSize: '0.8em' }}
                                    disabled={hasPendingPayout || isCheckingPendingPayout}
                                    onClick={async () => {
                                      setProcessing(true);
                                      setError(null);
                                      try {
                                        const requestPayout = httpsCallable(functionsInstance, "requestPayout");
                                        const payoutData = {
                                          userId: authUser.user.uid,
                                          amount: typeof earnings === 'number' ? earnings : undefined,
                                          currencyCode: "USD",
                                          orderIds: orderIds && orderIds.length > 0 ? orderIds : undefined
                                        };
                                        const result = await requestPayout(payoutData);
                                        if (result && result.data && result.data.success) {
                                          setError(null);
                                          alert("Payout request submitted successfully.");
                                          setHasPendingPayout(true); // disable button after request
                                        } else {
                                          setError("Failed to submit payout request.");
                                        }
                                      } catch (err) {
                                        setError(err.message || "Error submitting payout request.");
                                      } finally {
                                        setProcessing(false);
                                      }
                                    }}
                                  >
                                    {isCheckingPendingPayout ? "Checking..." : hasPendingPayout ? "Payout Pending" : "Request Payout"}
                                  </LoadingButton>
                                  {error && (
                                    <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>
                                  )}
                                </Box>
                              </>
                            )}
                          </Typography>
                        </Grid>
                        <Grid xs={12} md={12}>
                          {/* Stripe connection status and actions */}
                          {isCheckingConnection && (
                            <Typography align="center" sx={{ mt: 2 }}>
                              Checking Stripe connection status...
                            </Typography>
                          )}
                          {!isCheckingConnection && hasCheckedConnectionStatus && (
                            <>
                              {isConnected ? (
                                <>
                                  <Typography align="center" sx={{ mb: 1 }}>
                                    Stripe Connected
                                  </Typography>
                                  {isLoadingBalance ? (
                                    <Typography align="center">Loading balance...</Typography>
                                  ) : stripeBalance ? (
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="h6">Stripe Balance</Typography>
                                      {stripeBalance.available && stripeBalance.available.length > 0 && (
                                        <Typography variant="body1">
                                          {stripeBalance.available.map((bal, idx) => (
                                            <span key={idx}>
                                              {bal.amount / 100} {bal.currency.toUpperCase()}<br/>
                                            </span>
                                          ))}
                                        </Typography>
                                      )}
                                      {stripeBalance.pending && stripeBalance.pending.length > 0 && (
                                        <Typography variant="body2" color="text.secondary">
                                          Pending: {stripeBalance.pending.map((bal, idx) => (
                                            <span key={idx}>
                                              {bal.amount / 100} {bal.currency.toUpperCase()}<br/>
                                            </span>
                                          ))}
                                        </Typography>
                                      )}
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Recent Transactions</Typography>
                                        {isLoadingTransactions ? (
                                          <Typography>Loading transactions...</Typography>
                                        ) : transactionsError ? (
                                          <Alert severity="error">{transactionsError}</Alert>
                                        ) : transactions && transactions.length > 0 ? (
                                          <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                          }}>
                                            {transactions.map((tx) => (
                                              <Paper key={tx.id} elevation={2} sx={{
                                                p: 2,
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' },
                                                alignItems: { sm: 'center' },
                                                gap: 2,
                                              }}>
                                                <Box sx={{ flex: 1, minWidth: 100, textAlign: { xs: 'left', sm: 'right' } }}>
                                                  {/* Net Amount */}
                                                  <Typography variant="h6" sx={{ color: tx.amount > 0 ? 'success.main' : 'error.main', fontWeight: 700 }}>
                                                    {typeof tx.amount === 'number' && typeof tx.fee === 'number'
                                                      ? `${((tx.amount - tx.fee) / 100).toFixed(2)} ${tx.currency ? tx.currency.toUpperCase() : ''}`
                                                      : (tx.amount !== undefined ? `${(tx.amount / 100).toFixed(2)} ${tx.currency ? tx.currency.toUpperCase() : ''}` : '--')}
                                                  </Typography>
                                                  {/* Amount and Fee */}
                                                  <Typography variant="body2" color="text.secondary">
                                                    Amount: {typeof tx.amount === 'number' ? `${(tx.amount / 100).toFixed(2)} ${tx.currency ? tx.currency.toUpperCase() : ''}` : '--'}
                                                  </Typography>
                                                  <Typography variant="body2" color="text.secondary">
                                                    Fee: {typeof tx.fee === 'number' ? `${(tx.fee / 100).toFixed(2)} ${tx.currency ? tx.currency.toUpperCase() : ''}` : '--'}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ flex: 2, minWidth: 100 }}>
                                                  <Typography variant="body2" color="text.secondary">
                                                    {tx.created ? new Date(tx.created * 1000).toLocaleString() : ''}
                                                  </Typography>
                                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    {tx.type || ''}
                                                  </Typography>
                                                  <Typography variant="body2" color="text.secondary">
                                                    {tx.status === 'available' ? 'Succeeded' : (tx.status || '')}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ flex: 3, minWidth: 120 }}>
                                                  <Typography variant="body2">
                                                    {tx.description || ''}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                                    ID: {tx.id}
                                                  </Typography>
                                                </Box>
                                              </Paper>
                                            ))}
                                          </Box>
                                        ) : (
                                          <Typography>No transactions found.</Typography>
                                        )}
                                      </Box>
                                    </Box>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    loading={processing}
                                    onClick={async () => {
                                      setProcessing(true);
                                      setError(null);
                                      try {
                                        const generateStripeConnectLink = httpsCallable(functionsInstance, "generateStripeConnectLink");
                                        const origin = window.location.origin;
                                        const result = await generateStripeConnectLink({ userId: authUser.user.uid, origin });
                                        if (result && result.data && result.data.url) {
                                          window.location.href = result.data.url;
                                        } else {
                                          setError("Failed to get Stripe onboarding link.");
                                        }
                                      } catch (err) {
                                        setError(err.message || "Error generating Stripe onboarding link.");
                                      } finally {
                                        setProcessing(false);
                                      }
                                    }}
                                  >
                                    Create Payout Account
                                  </LoadingButton>
                                  {error && (
                                    <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                                  )}
                                  {/*connectionError && (
                                    <Alert severity="error" sx={{ mt: 2 }}>{connectionError}</Alert>
                                  )*/}
                                </>
                              )}
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </PageSection>
              </Grid>
            </Grid>
          </Box>
          <Footer />
        </Container>
      ) : (
        <>{loader}</>
      )}
    </>
  );
}

export default Payment;

function createData(name, calories, fat, price, orders) {
    const historyData = [];
    orders.map((item, index) => {
        historyData.push({date: item.date, customerId: item.fullname, amount: 1});
    });
    return {
      name,
      calories,
      fat,
      price,
      history: historyData,
    };
  }
  
  function Row(props) {
    const { row } = props;
    const [open, setOpen] = React.useState(false);
  
    return (
      <React.Fragment>
        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
          <TableCell>
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </TableCell>
          <TableCell component="th" scope="row">
            {row.name}
          </TableCell>
          <TableCell align="right">{row.calories}</TableCell>
          <TableCell align="right">{row.fat}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 1 }}>
                <Typography variant="h6" gutterBottom component="div">
                  History
                </Typography>
                <Table size="small" aria-label="purchases">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Price ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {row.history.map((historyRow) => (
                      <TableRow key={historyRow.date}>
                        <TableCell component="th" scope="row">
                          {historyRow.date}
                        </TableCell>
                        <TableCell>{historyRow.customerId}</TableCell>
                        <TableCell align="right">
                          {Math.round(historyRow.amount * row.price * 100) / 100}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }
  
  Row.propTypes = {
    row: PropTypes.shape({
      calories: PropTypes.number.isRequired,
      fat: PropTypes.number.isRequired,
      history: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number.isRequired,
          customerId: PropTypes.string.isRequired,
          date: PropTypes.string.isRequired,
        }),
      ).isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
    }).isRequired,
  };
  
function CollapsibleTable(props) {
    const orderedProducts = props.orderedProducts;
    //console.log('orderedProducts', orderedProducts);

    const rows = Object.keys(orderedProducts).map(key => {
            const orders = orderedProducts[key];
            console.log('orders', orders);
            let amount=0;
            let productName = '';
            let numberOfOrders = 0;
            let price = 0.0;
            orders.map((item, index) => {
                amount += item.totalPrice;
                productName = item.treatment.name;
                numberOfOrders ++;
                price = item.treatment.price;
            });

            return createData(productName, numberOfOrders, amount, price, orders);
        }
    );

    console.log('rows', rows);

    return (
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell align="right">Orders</TableCell>
              <TableCell align="right">Amount&nbsp;($)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <Row key={row.name} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }